import React, { useEffect, useMemo, useState } from "react"
import {
    Card,
    CardContent,
    CardHeader,
    Divider,
    Grid,
    TextField,
    Button,
    Box,
    InputAdornment,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Stack,
} from "@mui/material"

/* =========================
   Countries (editável)
========================= */
const COUNTRIES = [
    { code: "BR", name: "Brasil", dial: "+55", flag: "🇧🇷", phone: "br" },
    { code: "US", name: "Estados Unidos", dial: "+1", flag: "🇺🇸", phone: "us" },
    { code: "PT", name: "Portugal", dial: "+351", flag: "🇵🇹", phone: "pt" },
    { code: "AR", name: "Argentina", dial: "+54", flag: "🇦🇷", phone: "ar" },
    { code: "CL", name: "Chile", dial: "+56", flag: "🇨🇱", phone: "cl" },
    { code: "MX", name: "México", dial: "+52", flag: "🇲🇽", phone: "mx" },
]

/* =========================
   Utils
========================= */
const onlyDigits = (v) => String(v ?? "").replace(/\D/g, "")

const formatCpf = (digits) => {
    const d = onlyDigits(digits).slice(0, 11)
    if (d.length <= 3) return d
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`
}

const formatCnpj = (digits) => {
    const d = onlyDigits(digits).slice(0, 14)
    if (d.length <= 2) return d
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
    if (d.length <= 12)
        return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`
}

const formatCpfCnpj = (value) => {
    const d = onlyDigits(value)
    return d.length <= 11 ? formatCpf(d) : formatCnpj(d)
}

// Máscaras de telefone (SEM DDI; só número nacional)
const formatPhone = (digits, phoneRule) => {
    const d = onlyDigits(digits)

    // BR: (AA) NNNN-NNNN ou (AA) NNNNN-NNNN
    if (phoneRule === "br") {
        const dd = d.slice(0, 2)
        const rest = d.slice(2, 11)
        if (!dd) return ""
        if (rest.length <= 4) return `(${dd}) ${rest}`
        if (rest.length <= 8) return `(${dd}) ${rest.slice(0, 4)}-${rest.slice(4)}`
        return `(${dd}) ${rest.slice(0, 5)}-${rest.slice(5)}`
    }

    // US: (AAA) BBB-CCCC
    if (phoneRule === "us") {
        const a = d.slice(0, 3)
        const b = d.slice(3, 6)
        const c = d.slice(6, 10)
        if (!a) return ""
        if (d.length <= 3) return `(${a}`
        if (d.length <= 6) return `(${a}) ${b}`
        return `(${a}) ${b}-${c}`
    }

    // PT: AAA AAA AAA
    if (phoneRule === "pt") {
        const x = d.slice(0, 9)
        if (!x) return ""
        if (x.length <= 3) return x
        if (x.length <= 6) return `${x.slice(0, 3)} ${x.slice(3)}`
        return `${x.slice(0, 3)} ${x.slice(3, 6)} ${x.slice(6)}`
    }

    // fallback simples
    const x = d.slice(0, 11)
    if (!x) return ""
    if (x.length <= 3) return x
    if (x.length <= 7) return `${x.slice(0, 3)} ${x.slice(3)}`
    return `${x.slice(0, 3)} ${x.slice(3, 7)} ${x.slice(7)}`
}

const inferCountryFromPrefix = (phone = "") => {
    const t = String(phone || "").trim()
    if (!t.startsWith("+")) return null
    const cleaned = t.replace(/[^\d+]/g, "")
    const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length)
    return sorted.find((c) => cleaned.startsWith(c.dial)) || null
}

const stripCountryPrefix = (phone = "", dial = "+55") => {
    const raw = String(phone || "").trim()
    if (!raw.startsWith("+")) return onlyDigits(raw)
    const cleaned = raw.replace(/[^\d+]/g, "")
    if (cleaned.startsWith(dial)) return onlyDigits(cleaned.slice(dial.length))
    return onlyDigits(cleaned.replace("+", ""))
}

export default function EditBeneficiaryForm({
                                                initialValues = {
                                                    name: "",
                                                    cpfCnpj: "",
                                                    phone: "",
                                                    email: "",
                                                    country: "BR",
                                                    countryPrefix: "+55",
                                                },
                                                onSubmit,
                                                onCancel,
                                            }) {
    // state VISUAL (mascarado)
    const [form, setForm] = useState({
        name: "",
        cpfCnpj: "",
        phone: "",
        email: "",
        country: "BR",
        countryPrefix: "+55",
    })

    useEffect(() => {
        const inferred = inferCountryFromPrefix(initialValues.phone)
        const countryCode = initialValues.country || inferred?.code || "BR"
        const countryObj = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0]

        // se vier +55..., remove; se vier só dígitos, mantém
        const phoneNational = stripCountryPrefix(initialValues.phone, countryObj.dial)

        setForm({
            name: initialValues.name || "",
            cpfCnpj: formatCpfCnpj(initialValues.cpfCnpj || ""),
            email: initialValues.email || "",
            country: countryObj.code,
            countryPrefix: countryObj.dial,
            phone: formatPhone(phoneNational, countryObj.phone),
        })
    }, [initialValues])

    const selectedCountry = useMemo(
        () => COUNTRIES.find((c) => c.code === form.country) || COUNTRIES[0],
        [form.country]
    )

    // "limpos" para validação e submit
    const cpfCnpjDigits = useMemo(() => onlyDigits(form.cpfCnpj), [form.cpfCnpj])
    const phoneDigits = useMemo(() => onlyDigits(form.phone), [form.phone])

    const cpfCnpjError = useMemo(() => {
        if (!cpfCnpjDigits) return ""
        if (cpfCnpjDigits.length < 11) return "CPF incompleto"
        if (cpfCnpjDigits.length > 11 && cpfCnpjDigits.length < 14) return "CNPJ incompleto"
        if (![11, 14].includes(cpfCnpjDigits.length)) return "CPF/CNPJ inválido"
        return ""
    }, [cpfCnpjDigits])

    const phoneError = useMemo(() => {
        if (!phoneDigits) return ""
        if (selectedCountry.phone === "br") {
            if (phoneDigits.length < 10) return "Telefone incompleto"
            if (phoneDigits.length > 11) return "Telefone inválido"
            return ""
        }
        if (selectedCountry.phone === "us") {
            if (phoneDigits.length < 10) return "Telefone incompleto"
            if (phoneDigits.length > 10) return "Telefone inválido"
            return ""
        }
        if (selectedCountry.phone === "pt") {
            if (phoneDigits.length < 9) return "Telefone incompleto"
            if (phoneDigits.length > 9) return "Telefone inválido"
            return ""
        }
        if (phoneDigits.length < 8) return "Telefone incompleto"
        return ""
    }, [phoneDigits, selectedCountry.phone])

    const handleChange = (e) => {
        const { name, value } = e.target

        if (name === "cpfCnpj") {
            setForm((prev) => ({ ...prev, cpfCnpj: formatCpfCnpj(value) }))
            return
        }

        if (name === "phone") {
            setForm((prev) => ({ ...prev, phone: formatPhone(value, selectedCountry.phone) }))
            return
        }

        setForm((prev) => ({ ...prev, [name]: value }))
    }

    const handleCountryChange = (e) => {
        const newCode = e.target.value
        const c = COUNTRIES.find((x) => x.code === newCode) || COUNTRIES[0]
        const digits = onlyDigits(form.phone)

        setForm((prev) => ({
            ...prev,
            country: c.code,
            countryPrefix: c.dial,
            phone: formatPhone(digits, c.phone),
        }))
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (cpfCnpjError || phoneError) return

        // ✅ payload SEM formato + sem prefixo no phone
        const payload = {
            name: form.name,
            email: form.email,
            country: form.country,
            countryPrefix: selectedCountry.dial, // separado
            cpfCnpj: cpfCnpjDigits, // só números
            phone: phoneDigits, // só números, SEM +55
        }

        onSubmit?.(payload)
    }

    return (
        <Card sx={{ maxWidth: 720, margin: "0 auto" }}>
            <CardHeader
                title={
                    <Typography variant="h6" sx={{ color: "#ff0066", fontWeight: 700 }}>
                        Beneficiário
                    </Typography>
                }
            />
            <Divider />
            <CardContent>
                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={3}>
                            <Typography variant="body1">Nome</Typography>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <TextField
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                fullWidth
                                variant="standard"
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <Typography variant="body1">CPF/CNPJ</Typography>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <TextField
                                name="cpfCnpj"
                                value={form.cpfCnpj}
                                onChange={handleChange}
                                fullWidth
                                variant="standard"
                                required
                                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                                error={Boolean(cpfCnpjError)}
                                helperText={cpfCnpjError || " "}
                                inputProps={{ inputMode: "numeric" }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <Typography variant="body1">Telefone</Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="standard" sx={{ minWidth: 140 }}>
                                <Select value={form.country} onChange={handleCountryChange}>
                                    {COUNTRIES.map((c) => (
                                        <MenuItem key={c.code} value={c.code}>
                                            <span style={{ marginRight: 8 }}>{c.flag}</span>
                                            {c.code} {c.dial}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>

                                <TextField
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    fullWidth
                                    variant="standard"
                                    required
                                    placeholder={
                                        selectedCountry.code === "BR"
                                            ? "(11) 98888-7777"
                                            : selectedCountry.code === "US"
                                                ? "(415) 555-1234"
                                                : "Digite o telefone"
                                    }
                                    error={Boolean(phoneError)}
                                    helperText={phoneError || " "}
                                    inputProps={{ inputMode: "numeric" }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <span style={{ marginRight: 6 }}>{selectedCountry.flag}</span>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {selectedCountry.dial}
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <Typography variant="body1">Email</Typography>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <TextField
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                fullWidth
                                variant="standard"
                                required
                                type="email"
                            />
                        </Grid>
                    </Grid>

                    <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                        {onCancel && (
                            <Button onClick={onCancel} color="inherit">
                                Cancelar
                            </Button>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={Boolean(cpfCnpjError) || Boolean(phoneError)}
                        >
                            Salvar
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    )
}
